import { router } from "expo-router";
import { useEffect } from "react";

import type {
  HostInboundMessageEvent,
  PlaybackState,
} from "$modules/expo-tv-connect-host";
import {
  ExpoTvConnectHostModule,
  PlatformKeyCode,
} from "$modules/expo-tv-connect-host";
import {
  IS_TV_CONNECT_HOST_SUPPORTED,
  TV_CONNECT_SEEK_STEP_S,
} from "$src/core/constants/tv-connect";
import type { PlayerInstance } from "$src/core/contexts/player";
import Logging from "$src/core/services/logging/logging";

const useTvConnectHostPlayerController = ({
  player,
  contentId,
  contentType,
  contentTitle,
  contentImageUrl,
  trailer = false,
}: {
  player: PlayerInstance | undefined;
  contentId: string;
  contentType: string;
  contentTitle?: string;
  contentImageUrl?: string;
  trailer?: boolean;
}) => {
  useEffect(() => {
    if (!IS_TV_CONNECT_HOST_SUPPORTED || !player) return undefined;

    const buildPlaybackState = (): PlaybackState => {
      const isAdInProgress = player.adInProgress;

      const { duration } = player;

      const isLive = duration === Infinity;
      const hasDuration =
        Number.isFinite(duration) && duration > 0 && duration !== Infinity;

      const isAtEnd =
        !isAdInProgress &&
        hasDuration &&
        !player.playing &&
        player.currentTime >= duration;
      if (isAtEnd) {
        return { status: "idle", ts: Date.now() };
      }

      const status = player.playing ? "playing" : "paused";

      return {
        status,
        currentTime: player.currentTime,
        duration: hasDuration ? duration : undefined,
        live: isLive,
        asset: {
          contentId,
          contentType,
          title: contentTitle,
          thumbnail: contentImageUrl,
        },
        ad: isAdInProgress,
        muted: player.muted,
        trailer,
        ts: Date.now(),
      };
    };

    const handleBroadcastPlaybackState = (
      overridePlaybackState?: Partial<PlaybackState>,
    ) => {
      const payload: PlaybackState =
        overridePlaybackState?.status === "idle"
          ? { status: "idle", ts: Date.now() }
          : {
              ...buildPlaybackState(),
              ...overridePlaybackState,
            };

      Logging.debug("[TvConnectHost] Broadcasting playback state");

      ExpoTvConnectHostModule.broadcast({
        type: "playbackState",
        payload,
      });
    };

    // The player owns the flag now; these only push the change to the remote.
    const handleAdBreakChange = () => {
      handleBroadcastPlaybackState();
    };

    const stopPlayback = () => {
      player.stop();
    };

    const handlePlay = () => {
      player.play();
      handleBroadcastPlaybackState();
    };

    const handlePause = () => {
      player.pause();
      handleBroadcastPlaybackState();
    };

    const handleStop = () => {
      stopPlayback();

      handleBroadcastPlaybackState({ status: "idle" });

      if (router.canGoBack()) {
        router.back();
      }
    };

    const handleMute = () => {
      player.setMuted(true);

      handleBroadcastPlaybackState();
    };

    const handleUnmute = () => {
      player.setMuted(false);

      handleBroadcastPlaybackState();
    };

    const handleSeekTo = (startOffsetSeconds: number) => {
      player.seek(startOffsetSeconds);

      handleBroadcastPlaybackState();
    };

    const handleSeekEvent = (
      message: Extract<HostInboundMessageEvent, { type: "seek" }>,
    ) => {
      const { startOffset } = message.payload;
      if (typeof startOffset !== "number") {
        throw new Error("Invalid seek startOffset");
      }

      handleSeekTo(startOffset);
    };

    const handleVolumeEvent = (
      message: Extract<HostInboundMessageEvent, { type: "volume" }>,
    ) => {
      const { action } = message.payload;

      if (action === "mute") {
        handleMute();
        return;
      }
      if (action === "unmute") {
        handleUnmute();
      }
    };

    const handleButtonPressEvent = (
      message: Extract<HostInboundMessageEvent, { type: "buttonPress" }>,
    ) => {
      const { key } = message.payload;

      switch (key) {
        case PlatformKeyCode.Play:
          handlePlay();
          break;
        case PlatformKeyCode.Pause:
          handlePause();
          break;
        case PlatformKeyCode.Stop:
          handleStop();
          break;
        case PlatformKeyCode.Rewind:
          handleSeekTo(player.currentTime - TV_CONNECT_SEEK_STEP_S);
          break;
        case PlatformKeyCode.FastForward:
          handleSeekTo(player.currentTime + TV_CONNECT_SEEK_STEP_S);
          break;
        default:
          break;
      }
    };

    const handleMessage = (message: HostInboundMessageEvent) => {
      try {
        Logging.debug("[TvConnectHost] Message received", { message });

        switch (message.type) {
          case "buttonPress": {
            handleButtonPressEvent(message);
            return;
          }
          case "seek": {
            handleSeekEvent(message);
            return;
          }
          case "volume": {
            handleVolumeEvent(message);
            return;
          }
          case "play":
          case "route": {
            return;
          }
          default: {
            Logging.error("Invalid TV connect message", { message });
            break;
          }
        }
      } catch (error) {
        Logging.error("Failed to handle TV connect message", {
          error,
          message,
        });
      }
    };

    const messageSubscription = ExpoTvConnectHostModule.addListener(
      "onMessage",
      handleMessage,
    );

    const remoteConnectedSubscription = ExpoTvConnectHostModule.addListener(
      "onRemoteConnected",
      (event) => {
        const payload = buildPlaybackState();
        if (payload.status === "idle") return;

        Logging.debug("[TvConnectHost] Sending playback state");

        ExpoTvConnectHostModule.send(event.clientId, {
          type: "playbackState",
          payload,
        });
      },
    );

    // `currentTime` updates are throttled to ~1/s, per the protocol.
    let lastTimeUpdate = 0;
    const handleTimeUpdate = () => {
      const now = Date.now();
      if (now - lastTimeUpdate < 1000) return;
      lastTimeUpdate = now;
      handleBroadcastPlaybackState();
    };

    const errorSubscription = ExpoTvConnectHostModule.addListener(
      "onError",
      (event) => {
        Logging.error("TvConnectHost/onError", { event });
      },
    );

    handleBroadcastPlaybackState();

    const handleStateChange = () => handleBroadcastPlaybackState();

    player.addEventListener("playing", handleStateChange);
    player.addEventListener("ended", handleStateChange);
    player.addEventListener("durationchange", handleStateChange);
    player.addEventListener("timeupdate", handleTimeUpdate);
    player.addEventListener("adbegin", handleAdBreakChange);
    player.addEventListener("adend", handleAdBreakChange);

    return () => {
      messageSubscription.remove();
      remoteConnectedSubscription.remove();
      errorSubscription.remove();

      player.removeEventListener("playing", handleStateChange);
      player.removeEventListener("ended", handleStateChange);
      player.removeEventListener("durationchange", handleStateChange);
      player.removeEventListener("timeupdate", handleTimeUpdate);
      player.removeEventListener("adbegin", handleAdBreakChange);
      player.removeEventListener("adend", handleAdBreakChange);

      stopPlayback();
      handleBroadcastPlaybackState({ status: "idle" });
    };
  }, [player, contentId, contentType, contentTitle, contentImageUrl, trailer]);
};

export default useTvConnectHostPlayerController;
